import { CloudType } from '../../types/alerts';
import awsLogo from '../../assets/aws-logo.svg';
import gcpLogo from "../../assets/gcp-logo.svg";
import azureLogo from "../../assets/azure-logo.svg";
import { ChevronDown, Check, Building2, RefreshCw } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../ui/common-ui/dropdown-menu';

export type Tenant = {
  tenantName: string;
};

export type CloudResource = {
  id: string;
  name: string;
};

export type CloudConfig = {
  type: CloudType;
  logo: string;
  name: string;
  resourceLabel: string;
  resources: CloudResource[];
  disabled?: boolean;
};

export const cloudConfigs: CloudConfig[] = [
  { 
    type: 'AWS', 
    logo: awsLogo, 
    name: 'AWS',
    resourceLabel: 'Account',
    resources: []
  },
  { 
    type: 'GCP', 
    logo: gcpLogo, 
    name: 'GCP',
    resourceLabel: 'Project',
    resources: []
  },
  { 
    type: 'AZURE', 
    logo: azureLogo, 
    name: 'Azure',
    resourceLabel: 'Subscription',
    resources: []
  },
];

interface CloudSelectorProps {
  tenantList: Tenant[];
  selectedTenant: Tenant | null;
  selectedCloud: CloudType;
  selectedResource: CloudResource | null;
  cloudResources: Record<CloudType, CloudResource[]>;
  onTenantChange: (tenant: Tenant) => void;
  onCloudChange: (cloud: CloudType) => void;
  onResourceChange: (cloud: CloudType, resource: CloudResource) => void;
  isFetchingAlerts?: boolean;
}

export function CloudSelector({ 
  tenantList,
  selectedTenant,
  selectedCloud, 
  selectedResource,
  cloudResources,
  onTenantChange,
  onCloudChange, 
  onResourceChange,
  isFetchingAlerts = false
}: CloudSelectorProps) {
  const currentCloudConfig = cloudConfigs.find(c => c.type === selectedCloud)!;
  const currentResources = cloudResources[selectedCloud] || [];

  return (
    <div className="space-y-3">
      {/* Row 1: Tenant Dropdown */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground w-14">Tenant:</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 px-3 py-1 rounded-lg bg-card border border-border shadow-sm hover:bg-accent/50 transition-all duration-200 h-9">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-medium">{selectedTenant?.tenantName || 'Select Tenant'}</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[180px] bg-popover">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b border-border mb-1">
              Tenant
            </div>
            {tenantList.map((tenant) => (
              <DropdownMenuItem
                key={tenant.tenantName}
                onClick={() => onTenantChange(tenant)}
                className="flex items-center justify-between gap-2 cursor-pointer"
              >
                <span className={selectedTenant?.tenantName === tenant.tenantName ? 'font-medium' : ''}>
                  {tenant.tenantName}
                </span>
                {selectedTenant?.tenantName === tenant.tenantName && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Row 2: Cloud Selector + Resource Dropdown on RIGHT */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground w-14">Cloud:</span>
        <div className="flex items-center gap-0.5 p-1 bg-card rounded-lg border border-border shadow-sm h-9">
          {cloudConfigs.map((cloud) => {
            const isSelected = selectedCloud === cloud.type;
            const resources = cloudResources[cloud.type] || [];
            const isDisabled = resources.length === 0;
            
            return (
              <button
                key={cloud.type}
                onClick={() => !isDisabled && onCloudChange(cloud.type)}
                disabled={isDisabled}
                className={`
                  flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all duration-200 font-medium h-7
                  ${isDisabled 
                    ? 'opacity-40 cursor-not-allowed'
                    : isSelected 
                      ? 'bg-background text-foreground shadow-md ring-1 ring-primary/30' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                  }
                `}
                title={isDisabled ? `No ${cloud.resourceLabel}s available` : undefined}
              >
                <img 
                  src={cloud.logo} 
                  alt={cloud.name} 
                  className={`h-4 w-auto transition-all duration-200 ${
                    isDisabled ? 'opacity-30 grayscale' : isSelected ? 'opacity-100' : 'opacity-60 grayscale'
                  }`}
                />
                <span className="text-sm">{cloud.name}</span>
              </button>
            );
          })}
        </div>

        {/* Resource Dropdown - on the RIGHT */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="flex items-center gap-2 px-3 py-1 rounded-lg bg-card border border-border shadow-sm hover:bg-accent/50 transition-all duration-200 h-9"
              disabled={currentResources.length === 0}
            >
              <span className="text-xs text-muted-foreground">{currentCloudConfig.resourceLabel}:</span>
              <span className="text-sm font-medium truncate max-w-[150px]">
                {selectedResource?.name || 'Select'}
              </span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px] bg-popover">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b border-border mb-1">
              {currentCloudConfig.resourceLabel}
            </div>
            {currentResources.map((resource) => {
              const isResourceSelected = selectedResource?.id === resource.id;
              return (
                <DropdownMenuItem
                  key={resource.id}
                  onClick={() => onResourceChange(selectedCloud, resource)}
                  className="flex items-center justify-between gap-2 cursor-pointer"
                >
                  <span className={isResourceSelected ? 'font-medium' : ''}>
                    {resource.name}
                  </span>
                  {isResourceSelected && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {isFetchingAlerts && (
          <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
    </div>
  );
}
